import { Box, Stack } from "@mui/material";
import { makeStyles } from "@mui/styles";
import { useRef, useEffect, useState, ReactNode } from "react";
import { ReactComponent as IconChevronLeft } from "../../assets/IconChevronLeft.svg";
import { ReactComponent as IconChevronRight } from "../../assets/IconChevronRight.svg";
import { IconButtonOutlined } from "../v2/utils/IconButtonOutlined";

type SimpleCarouselProps = {
  children: ReactNode;
  gap?: number | string;
};

const useStyles = makeStyles(() => ({
  root: {
    position: "relative",
    width: "100%",
  },
  scrollContainer: {
    overflow: "hidden",
    flexWrap: "nowrap",
    zIndex: 0,
  },
  controlsButton: {
    width: 32,
    height: 32,
    position: "absolute",
    top: "50%",
    transform: "translateY(-50%)",
    backgroundColor: "#ffffff",
    boxShadow: "0px 4px 4px 0px #00000040",
    zIndex: 10,
  },
  previousButton: {
    left: 8,
  },
  nextButton: {
    right: 8,
  },
}));

export const SimpleCarousel = ({ children, gap = "8px" }: SimpleCarouselProps) => {
  const classes = useStyles();
  const stackRef = useRef<HTMLDivElement | null>(null);
  const [showArrows, setShowArrows] = useState(false);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const updateScrollState = () => {
    const el = stackRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 0);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth);
  };

  useEffect(() => {
    const el = stackRef.current;
    if (!el) return;
    updateScrollState();
    el.addEventListener("scroll", updateScrollState);
    window.addEventListener("resize", updateScrollState);
    return () => {
      el.removeEventListener("scroll", updateScrollState);
      window.removeEventListener("resize", updateScrollState);
    };
  }, []);

  const scrollLeft = () => {
    stackRef.current?.scrollBy({ left: -200, behavior: "smooth" });
  };

  const scrollRight = () => {
    stackRef.current?.scrollBy({ left: 200, behavior: "smooth" });
  };

  return (
    <Box
      className={classes.root}
      onMouseEnter={() => setShowArrows(true)}
      onMouseLeave={() => setShowArrows(false)}>
      <Stack ref={stackRef} direction="row" className={classes.scrollContainer} gap={gap}>
        {children}
      </Stack>

      {showArrows && canScrollLeft && (
        <IconButtonOutlined
          className={`${classes.controlsButton} ${classes.previousButton}`}
          onClick={scrollLeft}>
          <IconChevronLeft style={{ width: 26, height: 26, color: "#0092E0" }} />
        </IconButtonOutlined>
      )}
      {showArrows && canScrollRight && (
        <IconButtonOutlined
          className={`${classes.controlsButton} ${classes.nextButton}`}
          onClick={scrollRight}>
          <IconChevronRight style={{ width: 26, height: 26, color: "#0092E0" }} />
        </IconButtonOutlined>
      )}
    </Box>
  );
};
